import React, { useEffect, useState } from "react";
import { Plus, Trash2, Sparkles, Loader2, Wand2, FileText } from "lucide-react";
import { notesApi, aiQuick } from "../api";

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    const list = await notesApi.list();
    setNotes(list);
    if (!active && list[0]) setActive(list[0]);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(async () => {
      setSaving(true);
      const updated = await notesApi.update(active.id, { title: active.title, content: active.content });
      setSaving(false);
      setNotes(ns => ns.map(n => n.id === updated.id ? updated : n));
    }, 700);
    return () => clearTimeout(t);
  }, [active?.title, active?.content]);

  const create = async () => {
    const n = await notesApi.create({ title: "New Note", content: "" });
    setNotes([n, ...notes]);
    setActive(n);
  };

  const remove = async (id) => {
    await notesApi.remove(id);
    const remain = notes.filter(n => n.id !== id);
    setNotes(remain);
    if (active?.id === id) setActive(remain[0] || null);
  };

  const runAI = async (mode) => {
    if (!active?.content?.trim()) return;
    setAiBusy(true);
    const prompts = {
      summarize: `Summarize the following note in 3 concise bullet points:\n\n${active.content}`,
      rewrite: `Rewrite the following note to be clearer and more professional:\n\n${active.content}`,
      expand: `Expand the following note with more detail and context:\n\n${active.content}`,
    };
    try {
      const r = await aiQuick(prompts[mode], "You are a writing assistant. Return only the requested text.");
      setActive({ ...active, content: active.content + "\n\n---\n[AI " + mode + "]\n" + r.text });
    } catch {}
    setAiBusy(false);
  };

  return (
    <div className="h-full flex flex-col md:flex-row" data-testid="app-notes">
      <div className="w-full md:w-60 h-40 md:h-auto border-b md:border-b-0 md:border-r border-white/10 flex flex-col">
        <div className="p-3 flex items-center justify-between">
          <span className="text-sm font-medium">Notes</span>
          <button data-testid="notes-new" onClick={create} className="btn-ghost p-1.5 rounded-md">
            <Plus size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2">
          {notes.map(n => (
            <button
              key={n.id}
              data-testid={`note-item-${n.id}`}
              onClick={() => setActive(n)}
              className={`w-full text-left px-3 py-2 rounded-md mb-1 ${active?.id === n.id ? "bg-white/10" : "hover:bg-white/5"}`}
            >
              <div className="flex items-start gap-2">
                <FileText size={13} className="mt-0.5 text-white/60" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{n.title || "Untitled"}</div>
                  <div className="text-[10px] text-white/40 truncate">{(n.content || "").slice(0, 40)}</div>
                </div>
              </div>
            </button>
          ))}
          {notes.length === 0 && <div className="text-xs text-white/40 px-3 py-6 text-center">No notes yet</div>}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {active ? (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
              <input
                data-testid="note-title"
                value={active.title}
                onChange={(e) => setActive({ ...active, title: e.target.value })}
                className="bg-transparent outline-none text-sm font-medium flex-1"
              />
              <div className="flex items-center gap-2">
                {saving && <span className="text-[10px] text-white/40">Saving…</span>}
                <button data-testid="note-ai-summarize" onClick={() => runAI("summarize")} disabled={aiBusy} className="btn-ghost text-xs flex items-center gap-1">
                  {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Summarize
                </button>
                <button data-testid="note-ai-rewrite" onClick={() => runAI("rewrite")} disabled={aiBusy} className="btn-ghost text-xs flex items-center gap-1">
                  <Wand2 size={12} /> Rewrite
                </button>
                <button data-testid="note-ai-expand" onClick={() => runAI("expand")} disabled={aiBusy} className="btn-ghost text-xs">Expand</button>
                <button data-testid="note-delete" onClick={() => remove(active.id)} className="btn-ghost text-xs text-red-400 flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
            <textarea
              data-testid="note-content"
              value={active.content}
              onChange={(e) => setActive({ ...active, content: e.target.value })}
              placeholder="Start typing..."
              className="flex-1 bg-transparent outline-none p-4 text-sm resize-none leading-relaxed"
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-white/40 text-sm">Create a note to get started</div>
        )}
      </div>
    </div>
  );
}
